import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loading } from '@bumble/app/components/Loading';
import { PageLoading } from '@bumble/app/components/PageLoading';
import { useApi } from '@bumble/app/hooks/use-api';

interface RouterCreateProps {
  handleClose: () => void;
}

export const RouterCreate = ({ handleClose }: RouterCreateProps) => {
  const api = useApi();
  const [loadingModalOpen, setLoadingModalOpen] = useState<boolean>(false);
  const [cpeName, setCpeName] = useState<string>('');
  const [productCode, setProductCode] = useState<string>('');
  const [shippingAddress, setShippingAddress] = useState<string>('');
  const [siteAddress, setSiteAddress] = useState<string>('');
  const [wanCidr, setWanCidr] = useState<string>('');
  const [wanGateway, setWanGateway] = useState<string>('');
  const [wanDns, setWanDns] = useState<string>('');
  const [linkUpSpeed, setLinkUpSpeed] = useState<string>('');
  const [linkDownSpeed, setLinkDownSpeed] = useState<string>('');
  const [wanSnat, setWanSnat] = useState<boolean>(true);
  const [lanCidr, setLanCidr] = useState<string>('');
  const [providerServiceID, setProviderServiceID] = useState<string>('');
  const [clientCircuitID, setClientCircuitID] = useState<string>('');
  const [localContactName, setLocalContactName] = useState<string>('');
  const [localContactEmail, setLocalContactEmail] = useState<string>('');
  const [dhcpClient, setDhcpClient] = useState<boolean>(false);
  const [pingDnsIp, setPingDnsIp] = useState<string>('');
  const [defaultGatewayIp, setDefaultGatewayIp] = useState<string>('');
  const [routerMode, setRouterMode] = useState<boolean>(false);
  const [wan_sfp, setWanSFP] = useState<string>('sfp_mmf');
  const [lan_sfp, setLanSFP] = useState<string>('sfp_mmf');
  const [useWanSFP, setUseWanSFP] = useState<boolean>(false);
  const [useLanSFP, setUseLanSFP] = useState<boolean>(false);
  /**
   * Define creating mode
   *
   * 0: default, no product code or 'BN-CPE-1000'
   * 1: 'BN-CPE-IP'
   * 2: 'BN_CPE_100'
   * 3: 'BN-CPE-1000F'
   */
  const [mode, setMode] = useState<number>(0);

  const appPost = useMutation(api.createCpeApiV1CreateCpePost, {
    onSuccess() {
      toast.success('CPE router created.');
      handleClose();
    },
    onError(error: any) {
      setLoadingModalOpen(false);
      if (Array.isArray((error as any).error.detail)) {
        (error as any).error.detail.forEach((el: any) =>
          toast.error(el.message, { autoClose: false, hideProgressBar: true }),
        );
      } else {
        toast.error((error as any).error.detail, { autoClose: false, hideProgressBar: true });
      }
    },
  });

  const handleSubmit = () => {
    let extra = {};
    switch (mode) {
      case 1:
        extra = {
          ping_dns_ip: pingDnsIp,
          ping_gw_ip: defaultGatewayIp,
        }
        break;
      case 2:
        extra = {
          shipping_address: shippingAddress,
          mode: routerMode ? "ROUTER" : "PASSTHROUGH",
          ...(routerMode ? {
            ...(dhcpClient ? {
              wan_dhcp_c: true,
            } : {
              wan_dhcp_c: false,
              wan_cidr_addr: wanCidr,
              wan_default_gw_ip: wanGateway,
              wan_dns_ip: wanDns,
            })
          } : {
            ...(dhcpClient ? {
              mgmt_dhcp_c: true,
            } : {
              mgmt_dhcp_c: false,
              mgmt_cidr_addr: wanCidr,
              mgmt_default_gw_ip: wanGateway,
              mgmt_dns_ip: wanDns,
            })
          }),
          ...(routerMode && {
            wan_snat: wanSnat,
            ...(lanCidr && { lan_cidr_addr: lanCidr }),
          })
        };
        break;
      case 3:
        if (isNaN(parseInt(linkUpSpeed))) {
          toast.error('Please enter a valid number for link up speed.');
          return;
        }
        if (isNaN(parseInt(linkDownSpeed))) {
          toast.error('Please enter a valid number for link down speed.');
          return;
        }
        extra = {
          shipping_address: shippingAddress,
          ...(useWanSFP && { wan_sfp }),
          ...(useLanSFP && { lan_sfp }),
          wan_tx_mbps: parseInt(linkUpSpeed),
          wan_rx_mbps: parseInt(linkDownSpeed),
          mode: routerMode ? "ROUTER" : "PASSTHROUGH",
          ...(routerMode ? {
            ...(dhcpClient ? {
              wan_dhcp_c: true,
            } : {
              wan_dhcp_c: false,
              wan_cidr_addr: wanCidr,
              wan_default_gw_ip: wanGateway,
              wan_dns_ip: wanDns,
            })
          } : {
            ...(dhcpClient ? {
              mgmt_dhcp_c: true,
            } : {
              mgmt_dhcp_c: false,
              mgmt_cidr_addr: wanCidr,
              mgmt_default_gw_ip: wanGateway,
              mgmt_dns_ip: wanDns,
            })
          }),
          ...(routerMode && {
            wan_snat: wanSnat,
            ...(lanCidr && { lan_cidr_addr: lanCidr }),
          })
        };
        break;
      default:
        if (isNaN(parseInt(linkUpSpeed))) {
          toast.error('Please enter a valid number for link up speed.');
          return;
        }
        if (isNaN(parseInt(linkDownSpeed))) {
          toast.error('Please enter a valid number for link down speed.');
          return;
        }
        extra = {
          shipping_address: shippingAddress,
          wan_tx_mbps: parseInt(linkUpSpeed),
          wan_rx_mbps: parseInt(linkDownSpeed),
          mode: routerMode ? "ROUTER" : "PASSTHROUGH",
          ...(routerMode ? {
            ...(dhcpClient ? {
              wan_dhcp_c: true,
            } : {
              wan_dhcp_c: false,
              wan_cidr_addr: wanCidr,
              wan_default_gw_ip: wanGateway,
              wan_dns_ip: wanDns,
            })
          } : {
            ...(dhcpClient ? {
              mgmt_dhcp_c: true,
            } : {
              mgmt_dhcp_c: false,
              mgmt_cidr_addr: wanCidr,
              mgmt_default_gw_ip: wanGateway,
              mgmt_dns_ip: wanDns,
            })
          }),
          ...(routerMode && {
            wan_snat: wanSnat,
            ...(lanCidr && { lan_cidr_addr: lanCidr }),
          })
        };
    }
    setLoadingModalOpen(true);
    appPost.mutate({
      cpe_name: cpeName,
      prod_code: productCode,
      site_address: siteAddress,
      provider_service_id: providerServiceID,
      client_circuit_id: clientCircuitID,
      contact_name: localContactName,
      contact_email: localContactEmail,
      ...extra
    });
  };

  const isDisabled = () => {
    switch (mode) {
      case 1:
        return !(cpeName && productCode && pingDnsIp);
      default:
        return !(cpeName && productCode && shippingAddress && siteAddress);
    }
  };

  const handleChangeMode = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setRouterMode(value === 'true');
  };

  const { isLoading, data: productCodeList } = useQuery(
    ['cpeProducts'],
    api.cpeProductsApiV1CpeProductsGet.bind(null, {}),
    { enabled: true },
  );

  useEffect(() => {
    switch (productCode) {
      case 'BN-CPE-IP':
        setMode(1);
        break;
      case 'BN_CPE_100':
        setMode(2);
        break;
      case 'BN-CPE-1000F':
        setMode(3);
        break;
      default:
        setMode(0);
        break;
    }
  }, [productCode]);

  useEffect(() => {
    if (productCodeList && Array.isArray(productCodeList.data)) {
      setProductCode(productCodeList.data[0]);
    }
  }, [productCodeList])

  if (isLoading) return <Loading />;
  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12} sm={8}>
          <TextField
            fullWidth
            id="device_name"
            label="Device Name"
            variant="outlined"
            value={cpeName}
            onChange={(e) => setCpeName(e.target.value)}
            autoFocus
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel id="product_code_label">Product Code</InputLabel>
            <Select
              labelId="product_code_label"
              id="product_code"
              label="Product Code"
              value={productCode}
              onChange={(e) => setProductCode(e.target.value)}
            >
              {productCodeList?.data.map((code: string) => (
                <MenuItem value={code} key={code}>
                  {code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {mode !== 1 && <Grid item xs={12}>
          <TextField
            fullWidth
            id="shipping_address"
            label="Shipping Address"
            variant="outlined"
            value={shippingAddress}
            onChange={(e) => setShippingAddress(e.target.value)}
          />
        </Grid>}
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="site_address"
            label="Site Address"
            variant="outlined"
            value={siteAddress}
            onChange={(e) => setSiteAddress(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="provider_service_id"
            label="Provider Service ID"
            variant="outlined"
            value={providerServiceID}
            onChange={(e) => setProviderServiceID(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="client_circuit_id"
            label="Client Circuit ID"
            variant="outlined"
            value={clientCircuitID}
            onChange={(e) => setClientCircuitID(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="local_contact_name"
            label="Local Contact Name"
            variant="outlined"
            value={localContactName}
            onChange={(e) => setLocalContactName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="local_contact_email"
            label="Local Contact Email"
            variant="outlined"
            value={localContactEmail}
            onChange={(e) => setLocalContactEmail(e.target.value)}
          />
        </Grid>
        {(mode === 0 || mode === 3) && <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="linkuploadspeed"
              label="Link Upload Speed (mbps)"
              variant="outlined"
              value={linkUpSpeed}
              onChange={(e) => setLinkUpSpeed(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="linkdownloadspeed"
              label="Link Download Speed (mbps)"
              variant="outlined"
              value={linkDownSpeed}
              onChange={(e) => setLinkDownSpeed(e.target.value)}
            />
          </Grid>
        </>}
        {mode === 1 && <>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="ping_dns_ip"
              label="Monitoring Public IP (ipv4) or DNS name"
              variant="outlined"
              value={pingDnsIp}
              onChange={(e) => setPingDnsIp(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              id="default_gateway_ip"
              label="Default Gateway IP"
              variant="outlined"
              value={defaultGatewayIp}
              onChange={(e) => setDefaultGatewayIp(e.target.value)}
            />
          </Grid>
        </>}
      </Grid>
      {mode === 3 && <>
        <Divider sx={{ my: 4 }} />
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" sx={{ mb: 4, display: 'inline-block', marginRight: '50px' }}>
            WAN Interface:
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={useWanSFP}
                onChange={(e) => setUseWanSFP(e.target.checked)}
              />
            }
            label="Use SFP Port (Eth4)"
          />
          {useWanSFP && (
            <FormControl component="fieldset" sx={{ mt: 2, display:'flex', paddingLeft: '175px' }}>
              <RadioGroup
                row
                value={wan_sfp}
                onChange={(e) => setWanSFP(e.target.value)}
              >
                <FormControlLabel value="sfp_mmf" control={<Radio />} label="Multimode Fiber (MMF) (Short range)" />
                <FormControlLabel value="sfp_smf" control={<Radio />} label="Single-Mode Fiber (SMF) (Long range)" />
              </RadioGroup>
            </FormControl>)
          }
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="body2" sx={{ mb: 4, display: 'inline-block', marginRight: '55px' }}>
            LAN Interface:
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={useLanSFP}
                onChange={(e) => setUseLanSFP(e.target.checked)}
              />
            }
            label="Use SFP Port (Eth5)"
          />
          {useLanSFP && (
            <FormControl component="fieldset" sx={{ mt: 2, display:'flex', paddingLeft: '175px' }}>
              <RadioGroup
                row
                value={lan_sfp}
                onChange={(e) => setLanSFP(e.target.value)}
              >
                <FormControlLabel
                  value="sfp_mmf"
                  control={<Radio />}
                  label="Multimode Fiber (MMF) (Short range)"
                />
                <FormControlLabel
                  value="sfp_smf"
                  control={<Radio />}
                  label="Single-Mode Fiber (SMF) (Long range)"
                />
              </RadioGroup>
            </FormControl>
          )}
        </Grid>
      </>}
      {mode !== 1 && <>
        <Divider sx={{ my: 4 }} />
        <FormControl fullWidth sx={{ mb: 4 }}>
          <RadioGroup
            row
            name="router-mode"
            onChange={handleChangeMode}
            value={routerMode}
          >
            <FormControlLabel value="false" control={<Radio />} label="Pass through" />
            <FormControlLabel value="true" control={<Radio />} label="Router mode" />
          </RadioGroup>
        </FormControl>
        <Typography variant="body2" sx={{ mb: 4 }}>
          {routerMode ? 'WAN Interface (Eth0)' : 'Management Interface'}
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={dhcpClient}
                  onChange={(e) => setDhcpClient(e.target.checked)}
                />
              }
              label="DHCP Client"
            />
          </Grid>
          {!dhcpClient && <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="wan_cidr_ip_address"
                label="IP Address in CIDR format (Ex: A.B.C.D/24)"
                variant="outlined"
                value={wanCidr}
                onChange={(e) => setWanCidr(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="default_gateway_ip"
                label="Default Gateway IP"
                variant="outlined"
                value={wanGateway}
                onChange={(e) => setWanGateway(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="dns_server_ip"
                label="DNS Server IP(s)"
                variant="outlined"
                value={wanDns}
                onChange={(e) => setWanDns(e.target.value)}
              />
            </Grid>
          </>
          }
          {routerMode && <Grid item xs={12}>
            <FormControlLabel
              sx={{ mb: 4 }}
              control={
                <Checkbox
                  checked={wanSnat}
                  onChange={(e) => setWanSnat(e.target.checked)}
                />
              }
              label="Enable Source NAT"
            />
          </Grid>}
        </Grid>
        {routerMode && <>
          <Divider sx={{ my: 4 }} />
          <Typography variant="body2" sx={{ mb: 8 }}>
            LAN Interface (Eth1)
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="lan_cidr_ip_address"
                label="IP Address in CIDR format (Ex: A.B.C.D/24)"
                variant="outlined"
                value={lanCidr}
                onChange={(e) => setLanCidr(e.target.value)}
              />
            </Grid>
          </Grid>
        </>}
      </>}
      <Box display="flex" justifyContent="space-between" sx={{ mt: 10 }}>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          type="submit"
          onClick={handleSubmit}
          disabled={isDisabled()}
        >
          Create
        </Button>
      </Box>
      {loadingModalOpen && <PageLoading content="Working on it..." />}
    </Box>
  );
};
